import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function Home() {
	const glitterRef = useRef(null);

	// Sparkle trail that follows the pointer. One canvas rather than DOM nodes:
	// the trail spawns particles on every move event, and thrashing the DOM at
	// that rate is what makes cursor-trail effects stutter.
	useEffect(() => {
		const canvas = glitterRef.current;
		if (!canvas) return undefined;
		const ctx = canvas.getContext('2d');
		if (!ctx) return undefined;

		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
		const COLORS = ['#ffc60a', '#fff3c4', '#ff0a0a', '#ffffff', '#ff8a3d'];
		const MAX = 240;

		let w = 0;
		let h = 0;
		const resize = () => {
			// cap DPR at 2 — beyond that the extra pixels cost more than they show
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			w = window.innerWidth;
			h = window.innerHeight;
			canvas.width = Math.round(w * dpr);
			canvas.height = Math.round(h * dpr);
			canvas.style.width = w + 'px';
			canvas.style.height = h + 'px';
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};
		resize();

		const particles = [];
		let raf = 0;
		let last = 0;
		let px = null;
		let py = null;

		const spawn = (x, y, speed) => {
			// a faster pointer throws a longer, denser tail
			const n = Math.min(6, 1 + Math.floor(speed / 13));
			for (let i = 0; i < n; i++) {
				const a = Math.random() * Math.PI * 2;
				const v = 0.3 + Math.random() * 1.7;
				particles.push({
					x: x + (Math.random() - 0.5) * 16,
					y: y + (Math.random() - 0.5) * 16,
					vx: Math.cos(a) * v,
					vy: Math.sin(a) * v - 0.45,
					life: 0,
					ttl: 520 + Math.random() * 680,
					size: 3 + Math.random() * 7,
					rot: Math.random() * Math.PI,
					spin: (Math.random() - 0.5) * 0.008,
					seed: Math.random() * Math.PI * 2,
					color: COLORS[(Math.random() * COLORS.length) | 0],
				});
			}
			if (particles.length > MAX) particles.splice(0, particles.length - MAX);
		};

		// Four-point sparkle: concave sides, drawn by pulling each quadratic
		// control point into the centre.
		const sparkle = (x, y, r, rot) => {
			ctx.save();
			ctx.translate(x, y);
			ctx.rotate(rot);
			ctx.beginPath();
			ctx.moveTo(0, -r);
			ctx.quadraticCurveTo(0, 0, r, 0);
			ctx.quadraticCurveTo(0, 0, 0, r);
			ctx.quadraticCurveTo(0, 0, -r, 0);
			ctx.quadraticCurveTo(0, 0, 0, -r);
			ctx.fill();
			ctx.restore();
		};

		const frame = (t) => {
			const dt = last ? Math.min(48, t - last) : 16;
			last = t;
			const f = dt / 16.67;

			ctx.clearRect(0, 0, w, h);
			ctx.globalCompositeOperation = 'lighter';

			for (let i = particles.length - 1; i >= 0; i--) {
				const p = particles[i];
				p.life += dt;
				if (p.life >= p.ttl) {
					particles.splice(i, 1);
					continue;
				}
				const k = p.life / p.ttl;
				const drag = Math.pow(0.965, f);
				p.vx *= drag;
				p.vy = p.vy * drag + 0.02 * f;
				p.x += p.vx * f;
				p.y += p.vy * f;
				p.rot += p.spin * dt;

				// fade out over the lifetime, with a flicker on top so they twinkle
				ctx.globalAlpha = (1 - k) * (0.55 + 0.45 * Math.sin(p.life * 0.02 + p.seed));
				ctx.fillStyle = p.color;
				ctx.shadowColor = p.color;
				ctx.shadowBlur = 8;
				sparkle(p.x, p.y, p.size * (1 - k * 0.55), p.rot);
			}

			ctx.globalAlpha = 1;
			ctx.shadowBlur = 0;
			ctx.globalCompositeOperation = 'source-over';

			// idle with no particles: drop the loop entirely instead of burning
			// a frame every 16ms on an empty canvas
			if (particles.length) {
				raf = requestAnimationFrame(frame);
			} else {
				raf = 0;
				last = 0;
			}
		};

		const onMove = (e) => {
			if (reduced.matches) return;
			const x = e.clientX;
			const y = e.clientY;
			const speed = px === null ? 0 : Math.hypot(x - px, y - py);
			px = x;
			py = y;
			spawn(x, y, speed);
			if (!raf) {
				last = 0;
				raf = requestAnimationFrame(frame);
			}
		};

		const onReducedChange = () => {
			if (!reduced.matches) return;
			particles.length = 0;
			ctx.clearRect(0, 0, w, h);
		};

		window.addEventListener('pointermove', onMove, { passive: true });
		window.addEventListener('resize', resize);
		reduced.addEventListener('change', onReducedChange);

		return () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('resize', resize);
			reduced.removeEventListener('change', onReducedChange);
			if (raf) cancelAnimationFrame(raf);
		};
	}, []);

	return (
		<main style={{ textAlign: 'center', paddingBottom: 20, position: 'relative' }}>
			{/* Add responsive rule: default 12em, 9em when viewport < 1000px */}
			<style>{`
				.home-title {
					/* inherit the app's global font (Comic Neue) */
					font-family: inherit;
					font-size: 7em;
					/* Explicit bottom margin: h1's default is 0.67em, which at 7em is ~75px
					   and opens a gap far wider than the one below the date. */
					margin-top: 100px;
					margin-bottom: 40px;
					display: inline-block;
				}
				.home-date { margin-top: 0; margin-bottom: 8px; }
				.home-title .gold-nine {
					color: #ffc60a;
					font-weight: 700;
					text-shadow:
						0 0 6px rgba(255, 198, 10, 0.55),
						0 0 14px rgba(255, 198, 10, 0.35);
				}
				@media (max-width: 999px) { .home-title { font-size: 11vw; } }

				/* Responsive footer */
				.home-footer { position: fixed; bottom: 0; left: 0; width: 100%; background: transparent; color: #222; box-sizing: border-box; z-index: 2147483647; pointer-events: none; padding: clamp(8px, 2vw, 16px); }
				.home-footer a, .home-footer button { pointer-events: auto; }
				.home-footer .footer-inner { max-width: 1100px; width: 100%; margin: 0 auto; display:flex; justify-content:flex-start; align-items:flex-end; padding-left:18px; box-sizing:border-box; }
				.home-footer .contact { display:flex; flex-direction:column; align-items:flex-start; gap:clamp(4px,0.8vw,8px); font-size:clamp(12px,1.6vw,16px); line-height:1.1; }
				@media (max-width:480px) { .home-footer .footer-inner{ padding-left:12px } .home-footer .contact{ font-size:11px; gap:6px } }



				/* ---- Chaos layer: scattered artworks, fixed behind the content ---- */
				/* z-index 0 keeps the collage above the app's translucent white background
				   wash (a negative index would be painted behind it and washed out);
				   every sibling of the layer is lifted to 1 so text stays on top. */
				.chaos-layer { position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
				main > *:not(.chaos-layer) { position:relative; z-index:1 }
				.chaos-slot { position:absolute; }
				.chaos-slot img { display:block; width:100%; height:auto; }

				/* Both artworks are squashed vertically: an explicit height under its natural
				   one, plus object-fit:fill so the picture distorts to match instead of
				   cropping. Natural ratios are 1294x768 (guercino) and 1920x960 (supper). */

				/* Guercino: centred backdrop, tilted slightly off square */
				.slot-guercino { top:32%; left:50%; transform:translate(-50%,-50%);
					width:min(82vw,1040px); mix-blend-mode:multiply; }
				.slot-guercino img { height:min(27vw,340px); object-fit:fill; }

				/* The circle sits exactly on top of the supper. One shared rule for both, so
				   the boxes cannot drift apart. The circle is later in the markup, which is
				   what paints it above. */
				.slot-supper, .slot-circle { top:58vh; left:50%; transform:translateX(-50%);
					width:min(62vw,780px); }
				.slot-supper img, .slot-circle img { height:min(19vw,240px); object-fit:fill; }

				/* The circle sits slightly inside the supper. Scaled about its own centre
				   rather than given a smaller box: the box stays identical to the supper's,
				   so the two remain concentric at every width without a second set of
				   offsets to keep in sync. */
				.slot-circle img { transform:scale(0.92); transform-origin:50% 50%; }

				@media (max-width:700px) {
					/* Supper and circle span exactly the window width so nothing is clipped, and
					   both are taller than their natural ratio at that width to keep the stretch.
					   Natural heights at 414px wide: 207px supper, 125px circle. */
					/* Guercino deliberately overruns the window — the layer's overflow:hidden
					   clips it, so it reads as a large crop rather than a fitted picture.
					   Natural height at 170vw would be ~101vw, so 84vw keeps a mild squash. */
					/* Guercino's bottom edge meets the supper's top edge: 36% up from the
					   bottom against 64% down from the top, of the same box.
					   Percentages, NOT vh. The layer is position:fixed, so its height is the
					   visible viewport, while vh measures the viewport with the browser chrome
					   retracted — taller. Mixing the two leaves a gap the size of the address
					   bar. Percentages resolve against the layer itself, so both edges land on
					   the same measurement whatever the chrome is doing.
					   Anchoring by bottom also avoids centring, whose position would depend on
					   the image height — and that height is in vw. */
					.slot-guercino { width:170vw; top:auto; bottom:36%; transform:translateX(-50%) }
					.slot-guercino img { height:84vw }
					.slot-supper, .slot-circle { width:100vw; top:64% }
					.slot-supper img, .slot-circle img { height:44vw }
				}

				/* Flags. This MUST sit after main > *:not(.chaos-layer) above: that selector
				   and this one score identically (0,1,1), so the tie is broken by source
				   order alone. Declared earlier, its position:relative wins and the image
				   stays in the flow, centred by text-align, ignoring every offset here. */
				main > .home-bottom-right-image { position:fixed; bottom:0; left:50%; right:auto; transform:translateX(-50%); width:min(15vw,110px); height:auto; pointer-events:none; opacity:0.2; }
				/* Mobile: bottom-right corner. left:auto and transform:none undo the desktop
				   centring, which would otherwise fight the right anchor. */
				@media (max-width:700px) { main > .home-bottom-right-image { bottom:0; left:auto; right:0; transform:none; width:min(19vw,84px); } }

				/* Pointer sparkle trail. Fixed to the viewport and never a click target;
				   sits above the page art but below the CTA and footer. Needs its own
				   main > rule to beat main > *:not(.chaos-layer), which would otherwise
				   force it back to position:relative. */
				main > .glitter-canvas { position:fixed; top:0; left:0; z-index:2147483645; pointer-events:none; }
				@media (prefers-reduced-motion: reduce) { main > .glitter-canvas { display:none } }

				/* CTA: sits above the chaos collage and every other layer, fully opaque.
				   The wrapper needs the raised index too: main > *:not(.chaos-layer)
				   gives it z-index 1 and that stacking context would otherwise cap the
				   button's own index. */
				main > .cta-wrap { z-index: 2147483646 }
				.cta-varaa { position:relative; z-index:2147483646; opacity:1; background:#fff; }

			`}</style>

			{/* Chaotic decorative collage — fixed layer behind all page content */}
			<div className="chaos-layer" aria-hidden="true">
				<div className="chaos-slot slot-guercino"><img src="/guercino.jpg" alt="" /></div>
				<div className="chaos-slot slot-supper"><img src="/The-Last-Supper-Restored-Da-Vinci_32x16.jpg" alt="" /></div>
				<div className="chaos-slot slot-circle"><img src="/b823e8e99f1473be5bcad534d1fa18ee.png" alt="" /></div>
			</div>

			{/* Pointer sparkle trail */}
			<canvas className="glitter-canvas" ref={glitterRef} aria-hidden="true" />

			<h1 className="home-title">Scherzo Vol. <span className="gold-nine">10</span></h1>
			<h3 className="home-date">27.-28.9.2026</h3>

			<img
				className="home-bottom-right-image"
				src="/two-crossed-flags-finland_437242-39309.jpg"
				alt="Finland crossed flags"
			/>

			{/* Big CTA button */}
			<div className="cta-wrap">
				<Link to="/book">
					<button type="button" className="cta-varaa" style={{ marginTop: 28, padding: '16px 28px', fontSize: '1.5rem', cursor: 'pointer' }}>
						Varaa pöytä
					</button>
				</Link>

				{/* Footer only on home page (fixed to bottom). Left-aligned vertical contact lines */}
				<footer className="home-footer" aria-label="Contact information">
					<div className="footer-inner">
						<div className="contact">
							<div><a href="tel:+358442911021" style={{ color: 'inherit', textDecoration: 'none' }}>0442911021</a></div>
							<div><a href="mailto:restaurant.scherzo@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>restaurant.scherzo@gmail.com</a></div>
							<div>Bona Fide, Vironkatu 8, 00170 Helsinki</div>
						</div>
					</div>
				</footer>
			</div>
		</main>
	);
}

export default Home;
